import { useEffect, useState } from "react";

import { OnboardingParams, isAxiosError, restProvider } from "@/api/rest";
import linkedInLogo from "@/assets/images/logos/LI-Logo.png";
import { AuthLayout } from "@/components/layouts";
import { Button } from "@/components/ui/Button";
import { ButtonLoader } from "@/components/ui/buttons/ButtonLoader";
import { isLinkedinUrl } from "@/lib/linkedin-redirect";

import { AuthedAs } from "./AuthedAs";

const isOnboardingParams = (params: any): params is OnboardingParams => {
  const hasStringHiringContext = typeof params.hiringContext == "string";
  const hasStringExtensionType = typeof params.extensionType == "string";
  const hasStringRedirectUrl = typeof params.redirectUrl == "string";
  const hasStringSignature = typeof params.signature == "string";
  return (
    hasStringHiringContext && hasStringExtensionType && hasStringRedirectUrl && hasStringSignature
  );
};

export const ThirdPartyConnectionView = () => {
  const [params, setParams] = useState<OnboardingParams>({
    redirectUrl: "",
    hiringContext: "",
    extensionType: "",
    signature: "",
  });
  const [error, setError] = useState("");
  const { acceptLinkedinConnection } = restProvider();
  const [connectionOK, setConnectionOK] = useState(false);
  const [redirectionInfo, setRedirectionInfo] = useState("");

  useEffect(() => {
    const parsedQueryString = Object.fromEntries(new URLSearchParams(window.location.search));
    if (isOnboardingParams(parsedQueryString)) {
      setParams(parsedQueryString);
    } else {
      setError(`Incorrect credentials! ${window.location.href}`);
    }
  }, []);

  const handleCancel = () => {
    if (!isLinkedinUrl(params.redirectUrl)) {
      setError("Could not redirect!");
    } else {
      window.location.href = params.redirectUrl;
    }
  };

  const handleAuthorise = async () => {
    const missingCredentials = Object.values(params).reduce((acc, cur) => {
      return cur === "";
    }, false);
    if (missingCredentials) {
      setError(`Incorrect credentials! ${window.location.href}`);
    } else {
      try {
        await acceptLinkedinConnection(params);
        setConnectionOK(true);
      } catch (e: unknown) {
        if (isAxiosError(e)) {
          setError((e?.response?.data as { errorMessage: string }).errorMessage);
        }
      }
    }
  };

  useEffect(() => {
    let timer = 3;
    if (!connectionOK) {
      return;
    }
    const interval = setInterval(() => {
      if (timer >= 0) {
        setRedirectionInfo(`You will be redirected to LinkedIn in ${timer}`);
        timer = timer - 1;
      } else {
        window.location.href = params.redirectUrl;
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [connectionOK, params]);

  return (
    <AuthLayout title="Connect LinkedIn Talent Hub">
      <div className="w-full max-w-md space-y-6 p-8 rounded-2xl bg-background/95 backdrop-blur-md shadow-elegant">
        <div className="space-y-4">
          <p className="text-lg lg:text-xl text-center lg:text-left leading-relaxed">
            <img
              src={linkedInLogo}
              alt="LinkedIn logo"
              className="inline-block h-6 lg:h-8 mr-1 align-text-bottom"
            />{" "}
            Talent Hub account wants to connect to your SkillPanel account.
          </p>

          <AuthedAs />

          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-5 mt-10">
            <ButtonLoader onClick={handleAuthorise}>Authorize</ButtonLoader>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>

          {error && (
            <div className="text-center text-sm font-semibold text-destructive-fg mt-5">
              {error}
            </div>
          )}

          {redirectionInfo && (
            <div className="text-center text-base text-muted-foreground mt-10">
              {redirectionInfo}
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};
